import React from "react";
import { HeatConsumptionDataResultDTO } from "../dtos/HeatConsumptionDataResultDTO";
import { useTranslation } from "react-i18next";
import MeasuredValueDTO from "features/shared/dtos/MeasuredValueDTO";
import { convertCodeShortToLong } from "features/shared/helpers/languageHelper";
import style from "./heatConsumptionsGraph.module.scss";
import { Bar } from "react-chartjs-2";
import { HeatConsumptionType } from "../enums/heatConsumptionType";
import { ChartDataSets } from "chart.js";
import colors from 'styles/colors.module.scss';
import { ColorClassSuffix } from "features/shared/enums/colorClassSuffix";

type Props = {
  heatConsumptions: Record<string, HeatConsumptionDataResultDTO>;
  showLegend?: boolean;
};

const HeatConsumptionsGraph = ({
  heatConsumptions,
  showLegend = true,
}: Props) => {
  // translations
  const { i18n } = useTranslation();
  const tTypes = useTranslation("consumption\\enums\\heatConsumptionType").t;
  const tLabels = useTranslation("consumption\\consumption").t;

  // helpers
  const getGraphOptions = () => {
    let graphOptions = {
      maintainAspectRatio: false,
      legend: {
        display: showLegend,
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    };

    return graphOptions;
  };

  const getDataObj = () => {
    let datasets: ChartDataSets[] = [];
    let axisLabels: string[] = [];
    let counter = 0;

    for (const [key, consumptionResult] of Object.entries(heatConsumptions)) {
      const consumptionType = key as HeatConsumptionType;
      const measuredConsumptions =
        consumptionResult.measuredData?.measuredValues!;

      if (counter == 0 && measuredConsumptions?.length > 0) {
        axisLabels = getAxisLabels(measuredConsumptions);
        counter++;
      }

      let dataset = getHeatConumptionsDataset(
        consumptionType,
        measuredConsumptions
      );

      datasets.push(dataset);
    }

    let data = {
      labels: axisLabels,
      datasets: datasets,
    };

    return data;
  };

  const getHeatConumptionsDataset = (
    type: HeatConsumptionType,
    data: MeasuredValueDTO[]
  ): ChartDataSets => {
    let dataSet: ChartDataSets = {
      label: `${tLabels("heatConsumptionsGraph.consumption")} ${tTypes(type)} ${getUnit(data)}`,
      data: getData(data),
      backgroundColor: getColor(type),
      borderWidth: 0,
      fill: true,
    };

    return dataSet;
  };

  const getAxisLabels = (values: MeasuredValueDTO[]) => {
    const language = convertCodeShortToLong(i18n.language);
    let result = values.map((item) => getDataLabel(item.from!, language));

    return result;
  };

  const getDataLabel = (date: Date, lang: string): string => {
    if (date) {
      let parsedDate = new Date(date);
      let year = parsedDate.getFullYear();
      let month = parsedDate.toLocaleDateString(lang, { month: "2-digit" });

      return `${month}/${year}`;
    }

    return "";
  };

  const getData = (values: MeasuredValueDTO[]) => {
    let result = values.map((item) => (item.value ? item.value : 0));
    return result;
  };

  const getColor = (type: HeatConsumptionType): string => {
    switch (type) {
      case "CentralHeating":
        return colors[ColorClassSuffix.CentralHeating];
      case "DomesticHotWater":
        return colors[ColorClassSuffix.DomesticHotWater];
      case "TotalHeat":
        return colors[ColorClassSuffix.TotalHeat];
      default:
        return "#CCCCCC";
    }
  };

  const getUnit = (values: MeasuredValueDTO[]) => {
    let item = values?.length > 0 ? values[0] : null;
    return item !== null ? `[${item.unit}]` : "";
  };

  // render
  return (
    <div className={style.container}>
      <Bar data={getDataObj()} options={getGraphOptions()} />
    </div>
  );
};

export default HeatConsumptionsGraph;
